"use client";
import { jsx as _jsx } from "react/jsx-runtime";
import { useQueryClient } from "@tanstack/react-query";
import { createContext, useCallback, useContext, useEffect, useMemo, useState, } from "react";
import { apiFetch, apiUrl, authHeaders, TOKEN_REFRESHED_EVENT, } from "@/lib/api-client";
import { disconnectDoctorStreamChat } from "@/lib/disconnect-doctor-stream-chat";
import { isAllowedLandingReturnUrl, redirectToLandingLoggedOut, } from "@/lib/landing-redirect";
import { extractRolesFromMeResponse, isDoctorFromMeResponse, } from "@/lib/roles";
const TOKEN_KEY = "access_token";
const REFRESH_TOKEN_KEY = "refresh_token";
function consumeTokenFromUrl() {
    var _a, _b;
    if (typeof window === "undefined") {
        return { accessToken: null, refreshToken: null };
    }
    const hash = window.location.hash.startsWith("#")
        ? window.location.hash.slice(1)
        : window.location.hash;
    if (!hash) {
        return { accessToken: null, refreshToken: null };
    }
    const params = new URLSearchParams(hash);
    const accessToken = ((_a = params.get("access_token")) === null || _a === void 0 ? void 0 : _a.trim()) || null;
    const refreshToken = ((_b = params.get("refresh_token")) === null || _b === void 0 ? void 0 : _b.trim()) || null;
    if (!accessToken) {
        return { accessToken: null, refreshToken: null };
    }
    window.history.replaceState(null, "", window.location.pathname + window.location.search);
    return { accessToken, refreshToken };
}
/** Cross-app logout signal from Karen (`?logout=1&return=...`). */
function consumeLogoutSignal() {
    var _a;
    if (typeof window === "undefined") {
        return { shouldLogout: false, returnTo: null };
    }
    const url = new URL(window.location.href);
    if (url.searchParams.get("logout") !== "1") {
        return { shouldLogout: false, returnTo: null };
    }
    const returnTo = ((_a = url.searchParams.get("return")) === null || _a === void 0 ? void 0 : _a.trim()) || null;
    url.searchParams.delete("logout");
    url.searchParams.delete("return");
    const next = `${url.pathname}${url.search}${url.hash}`;
    window.history.replaceState(null, "", next || url.pathname);
    return { shouldLogout: true, returnTo };
}
function getStoredToken() {
    if (typeof window === "undefined")
        return null;
    return localStorage.getItem(TOKEN_KEY);
}
function getStoredRefreshToken() {
    if (typeof window === "undefined")
        return null;
    return localStorage.getItem(REFRESH_TOKEN_KEY);
}
function setStoredToken(token) {
    if (typeof window === "undefined")
        return;
    if (token)
        localStorage.setItem(TOKEN_KEY, token);
    else
        localStorage.removeItem(TOKEN_KEY);
}
function setStoredRefreshToken(token) {
    if (typeof window === "undefined")
        return;
    if (token)
        localStorage.setItem(REFRESH_TOKEN_KEY, token);
    else
        localStorage.removeItem(REFRESH_TOKEN_KEY);
}
let userInfoRequest;
const AuthContext = createContext(null);
async function fetchUserInfo(token) {
    if ((userInfoRequest === null || userInfoRequest === void 0 ? void 0 : userInfoRequest.token) === token) {
        return userInfoRequest.promise;
    }
    const promise = (async () => {
        try {
            const res = await fetch(apiUrl("/v1/me"), {
                headers: authHeaders(token),
            });
            if (!res.ok)
                return null;
            const data = await res.json();
            return {
                roles: extractRolesFromMeResponse(data),
                isDoctor: isDoctorFromMeResponse(data),
            };
        }
        catch (_a) {
            return null;
        }
    })();
    userInfoRequest = { token, promise };
    try {
        return await promise;
    }
    finally {
        if ((userInfoRequest === null || userInfoRequest === void 0 ? void 0 : userInfoRequest.promise) === promise) {
            userInfoRequest = undefined;
        }
    }
}
async function revokeRefreshSession(refreshToken) {
    try {
        await apiFetch("/api/auth/logout", {
            method: "POST",
            body: JSON.stringify({ refresh_token: refreshToken }),
            // Survives the hard redirect right after logout.
            keepalive: true,
        });
    }
    catch (_a) {
        // Local session is cleared either way; ignore network failures.
    }
}
export function AuthProvider({ children }) {
    const queryClient = useQueryClient();
    const logoutSignal = useMemo(() => consumeLogoutSignal(), []);
    const urlTokens = useMemo(() => {
        if (logoutSignal.shouldLogout) {
            return { accessToken: null, refreshToken: null };
        }
        return consumeTokenFromUrl();
    }, [logoutSignal.shouldLogout]);
    const [token, setTokenState] = useState(() => {
        if (logoutSignal.shouldLogout) {
            // The backend revokes only the session being logged out, so the cabinet
            // must revoke its own session when the landing logs out.
            const refresh = getStoredRefreshToken();
            setStoredToken(null);
            setStoredRefreshToken(null);
            if (refresh)
                void revokeRefreshSession(refresh);
            return null;
        }
        if (urlTokens.accessToken) {
            setStoredToken(urlTokens.accessToken);
            if (urlTokens.refreshToken) {
                setStoredRefreshToken(urlTokens.refreshToken);
            }
            return urlTokens.accessToken;
        }
        return getStoredToken();
    });
    const [userInfo, setUserInfo] = useState(null);
    const [isReady, _setIsReady] = useState(() => typeof window !== "undefined");
    const setToken = useCallback((value) => {
        setTokenState(value);
        setStoredToken(value);
        setUserInfo(null);
    }, []);
    const refetchUser = useCallback(async () => {
        const t = getStoredToken();
        if (!t) {
            setUserInfo(null);
            return;
        }
        const nextUserInfo = await fetchUserInfo(t);
        if (getStoredToken() === t) {
            setUserInfo(nextUserInfo);
        }
    }, []);
    useEffect(() => {
        if (logoutSignal.shouldLogout) {
            void disconnectDoctorStreamChat();
            queryClient.clear();
            const returnTo = logoutSignal.returnTo;
            if (returnTo && isAllowedLandingReturnUrl(returnTo)) {
                window.location.href = returnTo;
                return;
            }
        }
        const t = getStoredToken();
        setTokenState(t);
        if (t)
            refetchUser();
    }, [logoutSignal, queryClient, refetchUser]);
    // Without this every request keeps sending the expired token first.
    useEffect(() => {
        const onRefreshed = () => setTokenState(getStoredToken());
        window.addEventListener(TOKEN_REFRESHED_EVENT, onRefreshed);
        return () => window.removeEventListener(TOKEN_REFRESHED_EVENT, onRefreshed);
    }, []);
    // Another tab (or cross-app logout bounce) cleared localStorage.
    useEffect(() => {
        const onStorage = (event) => {
            if (event.key !== TOKEN_KEY)
                return;
            if (event.newValue)
                return;
            void disconnectDoctorStreamChat();
            queryClient.clear();
            setTokenState(null);
            setUserInfo(null);
        };
        window.addEventListener("storage", onStorage);
        return () => window.removeEventListener("storage", onStorage);
    }, [queryClient]);
    const logout = useCallback(async () => {
        const refresh = getStoredRefreshToken();
        void disconnectDoctorStreamChat();
        queryClient.clear();
        setToken(null);
        setStoredRefreshToken(null);
        if (refresh) {
            await revokeRefreshSession(refresh);
        }
    }, [queryClient, setToken]);
    /**
     * Clear storage + revoke without updating React token state first, then hard-navigate.
     * Updating React state first races with ProtectedRoute → LandingPage, which redirects to
     * Karen without ?logout=1 and leaves the marketing site logged in.
     */
    const logoutToLanding = useCallback(async () => {
        const refresh = getStoredRefreshToken();
        try {
            sessionStorage.setItem("bloom_logout_sync", "1");
        }
        catch (_a) {
            // ignore quota / private mode
        }
        void disconnectDoctorStreamChat();
        queryClient.clear();
        setStoredToken(null);
        setStoredRefreshToken(null);
        try {
            if (refresh) {
                await revokeRefreshSession(refresh);
            }
        }
        finally {
            redirectToLandingLoggedOut();
        }
    }, [queryClient]);
    const value = useMemo(() => ({
        token,
        userInfo,
        setToken,
        logout,
        logoutToLanding,
        isReady,
        refetchUser,
    }), [token, userInfo, setToken, logout, logoutToLanding, isReady, refetchUser]);
    return _jsx(AuthContext.Provider, { value: value, children: children });
}
export function useAuth() {
    const ctx = useContext(AuthContext);
    if (!ctx)
        throw new Error("useAuth must be used within AuthProvider");
    return ctx;
}
